import { Button, Grid, Popover, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import React, { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import { OrderDirection } from '../Datatable/OrderDirection';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { ContactSearchSelector, setCheckedContacts, setDatatableSelectedContactIndex, setOrderBy, setOrderDirection, setTriggerContactSearch } from './reducers/ContactSearchReducer';
import { contactInputSelector, setSelectedContactId } from './reducers/ContactInputReducer';
import ContactModel from './models/ContactModel';
import { userProfileSelector } from '../Login/reducers/UserProfileReducer';
import Swal from 'sweetalert2';
import ContactApi, { DeleteContactByIdParam } from './api/ContactApi';

const ContactTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let history = useHistory();

    let contactData = useSelector(ContactSearchSelector);
    const contactInputViewData = useSelector(contactInputSelector);
    const userProfile = useSelector(userProfileSelector);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [overviewFullTc, setOverviewFullTc] = useState<string>("");

    const divRef = React.createRef<HTMLDivElement>();

    const columns = [
        {
            Field: "companyName",
            Title: "Contact Name",
            AllowSort: true,
        } as DatatableColumnModel<ContactModel>,
        {
            Field: "subAcct",
            Title: "Sub Acct",
            AllowSort: true,
        } as DatatableColumnModel<ContactModel>,
        {
            Field: "keywords",
            Title: "Keywords",
            AllowSort: true,
        } as DatatableColumnModel<ContactModel>,
        {
            Field: "companyType",
            Title: "Comp Type",
            AllowSort: true,
        } as DatatableColumnModel<ContactModel>,
        {
            Field: "country",
            Title: "Country",
            AllowSort: true,
        } as DatatableColumnModel<ContactModel>,
        {
            Field: "interested",
            Title: "Interested",
            AllowSort: true,
        } as DatatableColumnModel<ContactModel>,
        {
            Field: "sizeFocus",
            Title: "Size Focus",
            AllowSort: true,
        } as DatatableColumnModel<ContactModel>,
        {
            Field: "emails",
            Title: "Emails",
            AllowSort: false,
            Width: "50px",
            Render: (rowData: ContactModel) => {
                return <>
                    <Button
                        className="table-emails"
                        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                            if (rowData.emails && rowData.emails.length > 0) {
                                setAnchorEl(event.currentTarget);
                                setOverviewFullTc(rowData.emails);
                            }
                        }}
                        onMouseLeave={() => {
                            setAnchorEl(null)
                        }}
                        disabled={!rowData.emails || rowData.emails.length === 0}
                    >
                        <i className="fa fa-envelope-o" aria-hidden="true"></i>
                    </Button>
                </>
            }
        } as DatatableColumnModel<ContactModel>,
        {
            Field: "userDisplayName",
            Title: "Broker",
            AllowSort: true,
        } as DatatableColumnModel<ContactModel>,
        {
            Field: "updatedDate",
            Title: "Updated Date",
            AllowSort: true,
            Render: (rowData: ContactModel) => (
                <>
                    {formatUpdatedDate(rowData.updatedDate).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<ContactModel>,
        {
            Field: "activeDate",
            Title: "Active Date",
            AllowSort: true,
            Render: (rowData: ContactModel) => (
                <>
                    {formatUpdatedDate(rowData.activeDate).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<ContactModel>,
        {
            Field: "",
            Title: "",
            AllowSort: true,
            Render: (rowData: ContactModel) => (
                <>
                    {userProfile.userGroup === "Administrator" &&
                        <Tooltip title="Delete Contact">
                            <Button
                                className='table-action-button'
                                variant='outlined'
                                color='error'
                                onClick={() => {
                                    deleteContact(rowData.id)
                                }}
                            >
                                <i className="fa fa-trash" aria-hidden="true"></i>
                            </Button>
                        </Tooltip>
                    }
                </>
            )
        } as DatatableColumnModel<ContactModel>,
    ];

    const deleteContact = (id: string) => {
        Swal.fire({
            title: 'Are you sure want to delete this market info? This action can not be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                ContactApi.Delete({
                    id: id
                } as DeleteContactByIdParam)
                    .then((response: BaseResponse<ContactModel>) => {
                        if (response.success) {
                            Utility.Alert.Success("Contact deleted successful");

                            dispatch(showLoading(false));

                            dispatch(setTriggerContactSearch(new Date().getTime()));
                        }
                    });
            }
        })
    }

    const formatUpdatedDate = (dateString: string): string => {
        if (!dateString) {
            return "";
        }
        let date = new Date(dateString);
        return moment(date).format("DD/MM/YYYY HH:mm");
    };

    const getTableClassName = () => {
        let className = "contact-datatable-search-result nice-scroll";

        if (!contactInputViewData.ContactInputExpand) {
            className += " contact-datatable-search-result-quick-input-collapse-only";
        }

        return className;
    }

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Grid>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        let emails = "";
                        _.each(contactData.checkedContacts, (tonnage) => {
                            emails += tonnage.emails + ";";
                        });
                        navigator.clipboard.writeText(emails);
                        Utility.Alert.Success("Copied to clipboard");
                    }}
                >Copy emails to clipboard</Button>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        let div = document.getElementById("tonnage-table-copy");
                        if (!div) {
                            return;
                        }

                        div.className = "tonnage-table-copy-show";

                        let selection = window.getSelection();
                        if (selection) {
                            let range = document.createRange();
                            range.selectNodeContents(divRef.current as Node);
                            selection.removeAllRanges();
                            selection.addRange(range);
                            document.execCommand("copy");
                            Utility.Alert.Success("Copied to clipboard");
                        }

                        div.className = "tonnage-table-copy-hide";
                    }}
                >Copy vessel list to clipboard</Button>
            </Grid>
            <Grid
                className='table-search-result-utility-bar-item table-search-result-utility-bar-result-found'
            >
                {contactData.searchResultTotalRecord + " results found"}
            </Grid>
        </Grid>
        <Datatable<ContactModel>
            useCheckBox={true}
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={contactData.searchResults}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<ContactModel>, orderDirection: OrderDirection) => {
                dispatch(setOrderBy(column.Field));
                dispatch(setOrderDirection(orderDirection));
                dispatch(setTriggerContactSearch((new Date()).getTime()));
            }}
            onRowSelectedChange={(tonnage: ContactModel, index: number, event: MouseEvent<HTMLElement>) => {
                var target = event.target as HTMLElement;
                if (target.tagName !== "BUTTON") {
                    dispatch(setSelectedContactId(tonnage.id))
                    dispatch(setDatatableSelectedContactIndex(index));
                }
            }}
            selectedRowIndex={contactData.datatableSelectedIndex}
            stickyHeader={true}
            orderColumn={contactData.query.orderBy}
            orderDirection={contactData.query.orderDirection}
            onTableCheckboxSelectedChange={(selectedRows: ContactModel[]) => {
                dispatch(setCheckedContacts(selectedRows));
            }}
        ></Datatable>
        <Popover
            id="mouse-over-popover"
            sx={{
                pointerEvents: 'none',
            }}
            open={anchorEl != null}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            disableRestoreFocus
        >
            <Typography sx={{ p: 1 }}>{overviewFullTc}</Typography>
        </Popover>
    </div>
};

export default ContactTable;